/**
 * ExportCacheManager - Temporary storage for rendered export images
 * 
 * Uses IndexedDB to store rendered image blobs temporarily during export process.
 * This allows exporting hundreds of photos without accumulating gigabytes in memory.
 * 
 * Flow:
 * 1. Render photo → Store blob in IndexedDB
 * 2. Strategy retrieves blob when needed
 * 3. After use (download/zip/write), delete blob from IndexedDB
 * 4. Auto-cleanup on export completion
 */

export class ExportCacheManager {
  constructor() {
    this.db = null;
    this.dbName = 'ExportCache';
    this.dbVersion = 1;
    this.storeName = 'exportBlobs';
    this.dbReady = this.initDB();
  }

  /**
   * Initialize IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('[ExportCacheManager] Database error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[ExportCacheManager] Database initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store for export blobs
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'photoId' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Store a rendered image blob
   * @param {number} photoId - Photo ID
   * @param {Blob} blob - Rendered image blob
   * @param {string} filename - Original filename for reference
   */
  async store(photoId, blob, filename) {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      
      const data = {
        photoId: photoId,
        blob: blob,
        filename: filename,
        timestamp: Date.now(),
        size: blob.size
      };
      
      const request = objectStore.put(data);
      
      request.onsuccess = () => {
        console.log(`[ExportCacheManager] Stored photo ${photoId} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
        resolve();
      };
      
      request.onerror = () => {
        console.error('[ExportCacheManager] Store error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Retrieve a rendered image blob
   * @param {number} photoId - Photo ID
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async retrieve(photoId) {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.get(photoId);
      
      request.onsuccess = () => {
        const data = request.result;
        if (data) {
          resolve({ blob: data.blob, filename: data.filename });
        } else {
          reject(new Error(`Photo ${photoId} not found in export cache`));
        }
      };
      
      request.onerror = () => {
        console.error('[ExportCacheManager] Retrieve error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a rendered image blob after use
   * @param {number} photoId - Photo ID
   */
  async delete(photoId) {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.delete(photoId);
      
      request.onsuccess = () => {
        console.log(`[ExportCacheManager] Deleted photo ${photoId}`);
        resolve();
      };
      
      request.onerror = () => {
        console.error('[ExportCacheManager] Delete error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached export blobs
   */
  async clearAll() {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.clear();
      
      request.onsuccess = () => {
        console.log('[ExportCacheManager] Cleared all cached blobs');
        resolve();
      };
      
      request.onerror = () => {
        console.error('[ExportCacheManager] Clear error:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get cache statistics
   * @returns {Promise<{count: number, totalSize: number}>}
   */
  async getStats() {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);
      const request = objectStore.getAll();
      
      request.onsuccess = () => {
        const items = request.result;
        const stats = {
          count: items.length,
          totalSize: items.reduce((sum, item) => sum + item.size, 0)
        };
        resolve(stats);
      };
      
      request.onerror = () => {
        console.error('[ExportCacheManager] Stats error:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
export default new ExportCacheManager();

